import React, { useEffect, useRef, useState } from 'react'
import { css } from '@styled-system/css'
import { CloseIcon } from 'components_v2/Icons'
import SortIcon from 'components_v2/Icons/SortIcon'
import { ChevronDown } from 'images/latest'
import styledComponents from 'styled-components'
import { Box, Button, Flex, P } from 'ui/acro'
import { useBodyScrollLock, useOutsideClick } from 'hooks'

const OpenSortButton = styledComponents(Flex)`
${css({
  backgroundColor: '#F2F2F2',
  borderRadius: '100px',
  alignItems: 'center',
  gap: '8px',
  padding: '5px 16px',
  fontWeight: '700',
  fontSize: '1em',
  width: 'fit-content',
  float: [null, 'right', 'right', 'right'],
  '.chevron-open': {
    svg: {
      transform: 'rotate(180deg)'
    }
  }
})}
`
const OpenSortButtonTitle = styledComponents(P)`
${css({
  whiteSpace: 'nowrap',
  wordBreak: 'inherit',
  fontWeight: ['500', '700'],
  fontSize: '1em',
  textAlign: 'center',
  textTransform: 'capitalize',
  color: 'black'
})}
`
const SortOptionBox = styledComponents(Flex)`
${css({
  zIndex: ['1003', '2', '2', '2'], // needs to be 1 higher than the overlay on mobile
  position: ['fixed', 'absolute', 'absolute', 'absolute'],
  right: '0',
  top: [null, '44px', '44px', '44px'],
  bottom: ['0', null, null, null],
  width: '100%',
  maxWidth: ['100%', '375px', '375px', '375px'],
  backgroundColor: 'white',
  boxShadow: `0px 4px 8px rgba(0, 0, 0, 0.06), 0px 12px 32px rgba(35, 41, 54, 0.14)`,
  flexDirection: 'column'
})}
`
const SortingOptions = styledComponents(Flex)`
${css({
  width: '100%',
  flexDirection: 'column',
  padding: '18px',
  alignItems: 'flex-start',
  gap: '30px'
})}
`
const SortingOptionTitle = styledComponents(Flex)`
${css({
  alignItems: 'center',
  display: 'flex',
  gap: '20px',
  fontFamily: 'Arquitecta',
  fontWeight: '400',
  fontSize: '16px',
  textTransform: 'uppercase',
  textAlign: 'left',
  'input[name="sortOption"]': {
    margin: '0',
    minWidth: '23px',
    minHeight: '23px',
    accentColor: 'black'
  },
  label: {
    '&:last-child': {
      borderBottom: '1px solid #E1E1E1'
    }
  }
})}
`
const SortButton = styledComponents(Button)`
${css({
  margin: ['10px 15px 40px 15px', '10px 15px 40px 15px'],
  borderRadius: '0',
  fontWeight: 400,
  fontSize: '15px'
})}
`
const OverlayContainer = styledComponents(Flex)`
${css({
  zIndex: ['1002', '1', '1', '1'], // needs to be 1 higher than the navbar on mobile
  display: ['block', 'none', 'none', 'none'],
  width: '100%',
  height: '100%',
  position: 'fixed',
  top: '0',
  left: '0',
  backgroundColor: '#242424',
  opacity: '.6'
})}
`

const MenuDialog = (props) => {
  const {
    showIcon = true,
    title = 'Sort',
    menuDialogOptions = [
      { text: 'Newest', prop: 'date' },
      { text: 'Relevant', prop: 'score' },
      { text: 'Popular', prop: 'rank' }
    ],
    location
  } = props

  const menuOptions = menuDialogOptions.map((option) => option.text)
  const [selected, setSelected] = useState(menuOptions[0])
  const [applied, setApplied] = useState(menuOptions[0])

  const height = `${166 + menuOptions.length * 53}px`
  const sortProp = menuDialogOptions.find((opt) => {
    return opt.text === applied
  }).prop

  const handleChange = (event) => {
    setSelected(event.target.value)
  }

  const [isOpen, setIsOpen] = useState(false)

  const toggleModal = () => {
    toggleBodyLock()
    setIsOpen((current) => !current)
  }
  const closeModal = () => {
    toggleBodyLock()
    setIsOpen(false)
  }
  const applySorting = () => {
    closeModal()
    setApplied(selected)
  }

  useEffect(() => {
    props.applied && props.applied(sortProp)
  }, [applied])

  // used to prevent body scrolling when sorting box is open
  const toggleBodyLock = useBodyScrollLock()

  const ref = useRef()
  useOutsideClick(ref, toggleModal, isOpen)

  return (
    <Box
      width='100%'
      position={['inherit', 'relative', 'relative', 'relative']}
    >
      <OpenSortButton onClick={toggleModal}>
        {showIcon && <SortIcon color='black' />}
        <OpenSortButtonTitle>{applied}</OpenSortButtonTitle>
        <Box className={isOpen ? 'chevron-open' : ''}>
          <ChevronDown color='black' />
        </Box>
      </OpenSortButton>
      {isOpen && (
        <Box ref={ref}>
          <SortOptionBox height={[null, height, height, height]}>
            <Flex
              alignItems='center'
              padding='15px 20px'
              borderBottom='1px solid #E1E1E1'
            >
              <P width='100%' textAlign='center' fontWeight='600'>
                {title}
              </P>
              <Box onClick={toggleModal}>
                <CloseIcon />
              </Box>
            </Flex>
            <SortingOptions id={'SortingForm'} value={applied} as='form'>
              {menuOptions.map((option) => {
                const defaultChecked = selected === option
                return (
                  <SortingOptionTitle
                    key={option}
                    as='label'
                    htmlFor={option}
                    onClick={handleChange}
                  >
                    <input
                      type='radio'
                      value={option}
                      name='sortOption'
                      id={option}
                      defaultChecked={defaultChecked}
                    />
                    {option}
                  </SortingOptionTitle>
                )
              })}
            </SortingOptions>
            <SortButton id={'ApplySortingButton'} onClick={applySorting}>
              Apply
            </SortButton>
          </SortOptionBox>
          <OverlayContainer onClick={closeModal} />
        </Box>
      )}
    </Box>
  )
}

export default MenuDialog
